import { IUser } from "../interfaces/user";
import { USER_ROLES } from "../interfaces/roles";
import { currencyFormatCentsToDollars } from "./currency";
import { ViewMode } from "../redux/actions/user/userAction";

/**
 * Check if user has a valid subscription that allows access to supplier/manufacturer views
 * @param profile - User profile object
 * @returns boolean indicating if user has valid subscription
 */
export const hasSupplierManufacturerAccess = (profile?: IUser): boolean => {
	if (!profile) return false;

	// Admin users always have access
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return true;
	}

	const subscriptionType = profile.company?.subscription_type;

	// Valid subscription types that allow supplier/manufacturer access
	const validSubscriptionTypes = [
		"supplier",
		"growth",
		"fresh",
		"enterprise",
		"guava"
	];

	return validSubscriptionTypes.includes(subscriptionType || "");
};

/**
 * Check if user has any active subscription
 * @param profile - User profile object
 * @returns boolean indicating if user has any subscription
 */
export const hasActiveSubscription = (profile?: IUser): boolean => {
	if (!profile) return false;

	// Admin users always have access
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return true;
	}

	const subscriptionType = profile.company?.subscription_type;
	return subscriptionType !== "" && subscriptionType !== null && subscriptionType !== undefined;
};

/**
 * Get subscription price and interval from subscription details
 * @param subscriptionDetails - Subscription details object from API
 * @returns Object with price and interval strings
 */
export const getSubscriptionPrice = (subscriptionDetails: any): { price: string; interval: string } => {
	if (!subscriptionDetails || !subscriptionDetails.latest_invoice) {
		return { price: "N/A", interval: "" };
	}

	const price = currencyFormatCentsToDollars(
		subscriptionDetails.latest_invoice.subtotal || 0,
		"USD"
	);

	// Extract interval from subscription (monthly/yearly)
	const interval = subscriptionDetails.subscription?.items?.data?.[0]?.price?.recurring?.interval || "month";

	return {
		price,
		interval: interval === "month" ? "month" : interval === "year" ? "year" : "month"
	};
};

/**
 * Check if an object is empty (no own properties)
 * @param obj - Object to check
 * @returns boolean indicating if object is empty
 */
export const isEmptyObject = (obj: any): boolean => {
	if (obj === null || obj === undefined) {
		return true;
	}

	if (typeof obj !== "object") {
		return false;
	}

	return Object.keys(obj).length === 0;
};

/**
 * Check if subscription has expired
 * @param profile - User profile object
 * @returns boolean indicating if subscription is expired
 */
export const isSubscriptionExpired = (profile?: IUser): boolean => {
	if (!profile) return true;

	// Admin users never have expired subscriptions
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return false;
	}

	const subscriptionPeriodEnd = profile.company?.subscription_period_end;
	const trialEndDate = profile.company?.trial_end_date;

	// If no end dates, assume not expired (might be lifetime or handled differently)
	if (!subscriptionPeriodEnd && !trialEndDate) {
		return false;
	}

	const now = new Date();

	// Check subscription period end
	if (subscriptionPeriodEnd) {
		const endDate = typeof subscriptionPeriodEnd === "string" 
			? new Date(subscriptionPeriodEnd) 
			: subscriptionPeriodEnd;
		if (endDate < now) {
			return true;
		}
	}

	// Check trial end date
	if (trialEndDate) {
		const trialEnd = typeof trialEndDate === "string" 
			? new Date(trialEndDate) 
			: trialEndDate;
		if (trialEnd < now) {
			return true;
		}
	}

	return false;
};

/**
 * Check if subscription supports supplier view
 * @param profile - User profile object
 * @returns boolean indicating if subscription supports supplier view
 */
export const canAccessSupplierView = (profile?: IUser): boolean => {
	if (!profile) return false;

	// Admin users can access all views
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return true;
	}

	// Check if subscription expired
	if (isSubscriptionExpired(profile)) {
		return false;
	}

	const subscriptionType = profile.company?.subscription_type;

	// Subscription types that support supplier view
	const supplierSubscriptionTypes = ["supplier", "guava"];

	return supplierSubscriptionTypes.includes(subscriptionType || "");
};

/**
 * Check if subscription supports manufacturer view
 * @param profile - User profile object
 * @returns boolean indicating if subscription supports manufacturer view
 */
export const canAccessManufacturerView = (profile?: IUser): boolean => {
	if (!profile) return false;

	// Admin users can access all views
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return true;
	}

	// Check if subscription expired
	if (isSubscriptionExpired(profile)) {
		return false;
	}

	const subscriptionType = profile.company?.subscription_type;

	// All valid subscription types support manufacturer view (except supplier-only)
	const manufacturerSubscriptionTypes = [
		"growth",
		"fresh",
		"enterprise",
		"guava",
		"supplier" // Supplier subscription can also access manufacturer view
	];

	return manufacturerSubscriptionTypes.includes(subscriptionType || "");
};

/**
 * Validate if user can access a specific view mode
 * @param viewMode - The view mode to check
 * @param profile - User profile object
 * @returns boolean indicating if user can access the view mode
 */
export const canAccessViewMode = (viewMode: ViewMode, profile?: IUser): boolean => {
	if (!profile) return false;

	if (viewMode === "supplier") {
		return canAccessSupplierView(profile);
	} else if (viewMode === "manufacturer") {
		return canAccessManufacturerView(profile);
	}

	return false;
};

/**
 * Get the default view mode based on subscription
 * @param profile - User profile object
 * @returns The default view mode for the user's subscription
 */
export const getDefaultViewMode = (profile?: IUser): ViewMode => {
	if (!profile) return "manufacturer";

	// Admin users default to manufacturer
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return "manufacturer";
	}

	// Check if subscription expired - default to manufacturer
	if (isSubscriptionExpired(profile)) {
		return "manufacturer";
	}

	const subscriptionType = profile.company?.subscription_type;

	// If user has supplier subscription, default to supplier view
	if (subscriptionType === "supplier" || subscriptionType === "guava") {
		return "supplier";
	}

	// Default to manufacturer for all other subscription types
	return "manufacturer";
};

/**
 * Validate and correct view mode based on subscription
 * @param currentViewMode - Current view mode
 * @param profile - User profile object
 * @returns Valid view mode (corrected if needed)
 */
export const validateAndCorrectViewMode = (
	currentViewMode: ViewMode,
	profile?: IUser
): ViewMode => {
	if (!profile) return "manufacturer";

	// Admin users can use any view mode
	if (profile.role === USER_ROLES.ADMIN || profile.role === USER_ROLES.JF_STAFF) {
		return currentViewMode;
	}

	// If subscription expired, force manufacturer view
	if (isSubscriptionExpired(profile)) {
		return "manufacturer";
	}

	// Check if current view mode is accessible
	if (canAccessViewMode(currentViewMode, profile)) {
		return currentViewMode;
	}

	// If not accessible, return default based on subscription
	return getDefaultViewMode(profile);
};
